// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import React, { useEffect, useRef, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { Link2, Loader2, CheckCircle2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const FB_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || "";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

function loadFacebookSdk() {
  return new Promise((resolve, reject) => {
    if (!FB_APP_ID) return reject(new Error("Facebook não configurado"));
    if (window.FB) return resolve(window.FB);
    window.fbAsyncInit = function () {
      window.FB.init({ appId: FB_APP_ID, cookie: false, xfbml: false, version: "v18.0" });
      resolve(window.FB);
    };
    if (document.getElementById("facebook-jssdk")) return;
    const js = document.createElement("script");
    js.id = "facebook-jssdk"; js.async = true; js.defer = true;
    js.src = "https://connect.facebook.net/pt_BR/sdk.js";
    js.onerror = () => reject(new Error("Falha ao carregar SDK do Facebook"));
    document.body.appendChild(js);
  });
}

export default function LinkedAccounts() {
  const [methods, setMethods] = useState(null);
  const [busy, setBusy] = useState("");
  const mounted = useRef(true);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/social/methods");
      setMethods(data);
    } catch (e) { console.debug("methods load:", e?.message); }
  };

  useEffect(() => {
    mounted.current = true;
    load();
    if (FB_APP_ID) loadFacebookSdk().catch(() => {});
    return () => { mounted.current = false; };
  }, []);

  if (!methods) return null;
  if (!GOOGLE_CLIENT_ID && !FB_APP_ID) return null;

  const has = (p) => (methods.providers || []).some((x) => x.provider === p);
  const emailOf = (p) => (methods.providers || []).find((x) => x.provider === p)?.email;

  const linkGoogle = async (resp) => {
    if (!resp?.credential) return;
    setBusy("google-link");
    try {
      await api.post("/auth/social/link/google", { credential: resp.credential });
      toast.success("Google conectado à sua conta.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao vincular Google.");
    } finally { setBusy(""); }
  };

  const linkFacebook = async () => {
    if (!window.FB) return toast.error("Facebook indisponível.");
    setBusy("facebook-link");
    window.FB.login(async (resp) => {
      if (!resp?.authResponse?.accessToken) { setBusy(""); return; }
      try {
        await api.post("/auth/social/link/facebook", { access_token: resp.authResponse.accessToken });
        toast.success("Facebook conectado à sua conta.");
        await load();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao vincular Facebook.");
      } finally { setBusy(""); }
    }, { scope: "public_profile,email" });
  };

  const unlink = async (provider) => {
    if (!window.confirm(`Desconectar ${provider === "google" ? "Google" : "Facebook"} desta conta?`)) return;
    setBusy(`${provider}-unlink`);
    try {
      await api.delete(`/auth/social/${provider}`);
      toast.success("Provedor desconectado.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao desconectar.");
    } finally { setBusy(""); }
  };

  const Row = ({ provider, label, connected, email, icon }) => (
    <div className="flex items-center gap-3 p-4" data-testid={`linked-${provider}`}>
      <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold flex items-center gap-2">
          {label}
          {connected && <CheckCircle2 className="w-4 h-4 text-success" />}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {connected ? (email || "Conectado") : "Não conectado"}
        </p>
      </div>
      <div>
        {provider === "password" ? (
          <span className="text-xs text-muted-foreground">
            {connected ? "Configurada" : "Não definida"}
          </span>
        ) : connected ? (
          <button
            data-testid={`linked-${provider}-unlink`}
            onClick={() => unlink(provider)}
            disabled={busy.startsWith(provider)}
            className="text-xs font-semibold text-alert hover:underline disabled:opacity-50"
          >
            {busy === `${provider}-unlink` ? "…" : "Desconectar"}
          </button>
        ) : provider === "google" && GOOGLE_CLIENT_ID ? (
          <div className="scale-90 origin-right">
            <GoogleLogin
              onSuccess={linkGoogle}
              onError={() => toast.error("Não foi possível vincular Google.")}
              size="medium" text="signin" locale="pt_BR" theme="outline"
            />
          </div>
        ) : provider === "facebook" && FB_APP_ID ? (
          <button
            data-testid="linked-facebook-connect"
            onClick={linkFacebook}
            disabled={busy === "facebook-link"}
            className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#1877F2] text-white hover:brightness-95 disabled:opacity-60"
          >
            {busy === "facebook-link" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Conectar"}
          </button>
        ) : null}
      </div>
    </div>
  );

  return (
    <div data-testid="linked-accounts-section">
      <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
        <Link2 className="w-5 h-5 text-primary" /> Formas de acesso
      </h2>
      <div className="rounded-3xl border border-border bg-card divide-y divide-border overflow-hidden">
        {GOOGLE_CLIENT_ID && (
          <Row
            provider="google" label="Google"
            connected={has("google")} email={emailOf("google")}
            icon={
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.1A6.98 6.98 0 015.47 12c0-.73.13-1.44.36-2.1V7.06H2.18A11 11 0 001 12c0 1.78.43 3.46 1.18 4.94l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
              </svg>
            }
          />
        )}
        {FB_APP_ID && (
          <Row
            provider="facebook" label="Facebook"
            connected={has("facebook")} email={emailOf("facebook")}
            icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2" aria-hidden>
                <path d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.407.593 24 1.325 24H12.82v-9.294H9.692V11.09h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.464.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.617h-3.12V24h6.116C23.407 24 24 23.407 24 22.676V1.325C24 .593 23.407 0 22.675 0z" />
              </svg>
            }
          />
        )}
        <Row
          provider="password" label="E-mail e senha"
          connected={methods.has_password} email={null}
          icon={<KeyRound className="w-5 h-5" />}
        />
      </div>
      {!methods.has_password && ((has("google") || has("facebook"))) && (
        <p className="text-xs text-muted-foreground mt-2 px-1">
          💡 Defina uma senha em "Trocar senha" para poder desconectar seu provedor social sem perder acesso.
        </p>
      )}
    </div>
  );
}
